import React, { useState, useEffect, useCallback } from 'react';
import {
    getAllTerms,
    createTerms,
    updateTerms,
    deleteTerms,
    getTermsAcceptanceLogs,
    exportTermsAcceptanceExcel,
    TermsAndCondition,
    CreateTermsRequest,
    TermsAcceptanceLog,
} from '../services/api';
import {
    FiFileText,
    FiPlus,
    FiEdit2,
    FiTrash2,
    FiSave,
    FiX,
    FiRefreshCw,
    FiUsers,
    FiDownload,
    FiList,
    FiSettings,
    FiCheckCircle,
} from 'react-icons/fi';
import './TermsAndConditions.css';

const IconFileText = FiFileText as any;
const IconPlus = FiPlus as any;
const IconEdit2 = FiEdit2 as any;
const IconTrash2 = FiTrash2 as any;
const IconSave = FiSave as any;
const IconX = FiX as any;
const IconRefreshCw = FiRefreshCw as any;
const IconUsers = FiUsers as any;
const IconDownload = FiDownload as any;
const IconList = FiList as any;
const IconSettings = FiSettings as any;
const IconCheckCircle = FiCheckCircle as any;

type TabView = 'terms' | 'acceptance-logs';
type TermsType = 'USER' | 'TECHNICIAN' | '';

interface TermsAndConditionsProps {
    initialView?: TabView;
}

const emptyForm: CreateTermsRequest = {
    contentAr: '',
    contentEn: '',
    type: 'USER',
    version: '',
    isActive: true,
};

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({ initialView = 'terms' }) => {
    const [activeView, setActiveView] = useState<TabView>(initialView);

    // Sync state if initialView prop changes (e.g. from sidebar clicking)
    useEffect(() => {
        setActiveView(initialView);
    }, [initialView]);

    /* ── Terms management state ── */
    const [terms, setTerms] = useState<TermsAndCondition[]>([]);
    const [loadingTerms, setLoadingTerms] = useState(false);
    const [typeFilter, setTypeFilter] = useState<TermsType>('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState<CreateTermsRequest>(emptyForm);
    const [expandedId, setExpandedId] = useState<number | null>(null);

    /* ── Acceptance logs state ── */
    const [logs, setLogs] = useState<TermsAcceptanceLog[]>([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [logsError, setLogsError] = useState('');

    /* ────────────────────────────────────────────
       TERMS MANAGEMENT
    ──────────────────────────────────────────── */

    const loadTerms = useCallback(async () => {
        try {
            setLoadingTerms(true);
            const data = await getAllTerms(typeFilter || undefined);
            setTerms(data.terms || []);
        } catch (err: any) {
            console.error('Failed to load terms:', err);
            alert(`Error loading terms: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoadingTerms(false);
        }
    }, [typeFilter]);

    useEffect(() => {
        loadTerms();
    }, [loadTerms]);

    const handleCreate = async () => {
        if (!formData.contentAr || !formData.contentEn || !formData.type || !formData.version) {
            alert('Please fill in all required fields (Arabic content, English content, type, and version).');
            return;
        }
        try {
            await createTerms(formData);
            await loadTerms();
            resetForm();
            alert('Terms and conditions created successfully!');
        } catch (err: any) {
            alert(`Error: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleUpdate = async (id: number) => {
        try {
            await updateTerms(id, formData);
            await loadTerms();
            resetForm();
            alert('Terms and conditions updated successfully!');
        } catch (err: any) {
            alert(`Error: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleDelete = async (id: number, version: string, type: string) => {
        if (!window.confirm(`Soft-delete Terms v${version} (${type})?\n\nThis will mark it as inactive.`)) return;
        try {
            await deleteTerms(id);
            await loadTerms();
            alert('Terms deleted (soft-deleted) successfully!');
        } catch (err: any) {
            alert(`Error: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleEdit = (t: TermsAndCondition) => {
        setEditingId(t.id);
        setFormData({
            contentAr: t.contentAr,
            contentEn: t.contentEn,
            type: t.type as 'USER' | 'TECHNICIAN',
            version: t.version,
            isActive: t.isActive,
        });
        setShowCreateForm(false);
        setExpandedId(null);
    };

    const resetForm = () => {
        setEditingId(null);
        setShowCreateForm(false);
        setFormData(emptyForm);
    };

    /* ────────────────────────────────────────────
       ACCEPTANCE LOGS
    ──────────────────────────────────────────── */

    const loadLogs = useCallback(async () => {
        try {
            setLoadingLogs(true);
            setLogsError('');
            const data = await getTermsAcceptanceLogs();
            setLogs(data.logs || []);
        } catch (err: any) {
            setLogsError(err.response?.data?.error || err.message || 'Failed to load acceptance logs');
        } finally {
            setLoadingLogs(false);
        }
    }, []);

    useEffect(() => {
        if (activeView === 'acceptance-logs') loadLogs();
    }, [activeView, loadLogs]);

    const handleExportExcel = async () => {
        try {
            const blob = await exportTermsAcceptanceExcel();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `terms_acceptance_logs_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err: any) {
            alert(`Export failed: ${err.response?.data?.error || err.message}`);
        }
    };

    /* ────────────────────────────────────────────
       HELPERS
    ──────────────────────────────────────────── */

    const formatDate = (ts: string) =>
        new Date(ts).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });

    const typeBadgeClass = (type: string) =>
        type === 'USER' ? 'type-badge user' : 'type-badge technician';

    /* ────────────────────────────────────────────
       RENDER
    ──────────────────────────────────────────── */

    return (
        <div className="terms-management">
            {/* Page Header */}
            <div className="terms-page-header">
                <div className="terms-header-left">
                    <div className="terms-header-icon">
                        {React.createElement(IconFileText, { size: 28 })}
                    </div>
                    <div>
                        <h2>Terms &amp; Conditions</h2>
                        <p>Manage terms for users and technicians, and view acceptance logs.</p>
                    </div>
                </div>
            </div>

            {/* Sub-tab Switcher */}
            <div className="terms-view-toggle">
                <button
                    className={`terms-tab-btn ${activeView === 'terms' ? 'active' : ''}`}
                    onClick={() => setActiveView('terms')}
                >
                    {React.createElement(IconSettings, { size: 16 })}
                    Terms Management
                </button>
                <button
                    className={`terms-tab-btn ${activeView === 'acceptance-logs' ? 'active' : ''}`}
                    onClick={() => setActiveView('acceptance-logs')}
                >
                    {React.createElement(IconCheckCircle, { size: 16 })}
                    Acceptance Logs
                </button>
            </div>

            {/* ============================
          VIEW: Terms Management
      ============================ */}
            {activeView === 'terms' && (
                <div>
                    {/* Toolbar */}
                    <div className="terms-toolbar">
                        <div className="terms-filter-row">
                            <label>Filter by type:</label>
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value as TermsType)}
                                className="terms-select"
                            >
                                <option value="">All Types</option>
                                <option value="USER">User</option>
                                <option value="TECHNICIAN">Technician</option>
                            </select>
                        </div>
                        <div className="terms-toolbar-actions">
                            <button className="btn-refresh-terms" onClick={loadTerms} disabled={loadingTerms}>
                                {React.createElement(IconRefreshCw, { className: loadingTerms ? 'spinning' : '', size: 16 })}
                                Refresh
                            </button>
                            <button
                                className="btn-add-terms"
                                onClick={() => { resetForm(); setShowCreateForm(true); }}
                            >
                                {React.createElement(IconPlus, { size: 16 })}
                                Add Terms
                            </button>
                        </div>
                    </div>

                    {/* Create Form */}
                    {showCreateForm && (
                        <div className="terms-form-card">
                            <h3>Create New Terms &amp; Conditions</h3>
                            <TermsForm
                                formData={formData}
                                setFormData={setFormData}
                                onSubmit={handleCreate}
                                onCancel={resetForm}
                                submitLabel="Create Terms"
                            />
                        </div>
                    )}

                    {/* Terms List */}
                    {loadingTerms ? (
                        <div className="terms-loading">
                            <div className="spinner" />
                            <p>Loading terms and conditions…</p>
                        </div>
                    ) : terms.length === 0 ? (
                        <div className="terms-empty">
                            {React.createElement(IconFileText, { size: 48 })}
                            <p>No terms and conditions found. Click "Add Terms" to create one.</p>
                        </div>
                    ) : (
                        <div className="terms-list">
                            {terms.map((t) => {
                                const isEditing = editingId === t.id;
                                const isExpanded = expandedId === t.id;

                                return (
                                    <div key={t.id} className={`terms-card ${!t.isActive ? 'inactive' : ''}`}>
                                        {isEditing ? (
                                            <div className="terms-edit-form">
                                                <h4>Edit Terms v{t.version}</h4>
                                                <TermsForm
                                                    formData={formData}
                                                    setFormData={setFormData}
                                                    onSubmit={() => handleUpdate(t.id)}
                                                    onCancel={resetForm}
                                                    submitLabel="Save Changes"
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                {/* Card Header */}
                                                <div className="terms-card-header">
                                                    <div className="terms-card-meta">
                                                        <span className={typeBadgeClass(t.type)}>{t.type}</span>
                                                        <span className="version-badge">v{t.version}</span>
                                                        <span className={`status-badge ${t.isActive ? 'active' : 'inactive'}`}>
                                                            {t.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                    <div className="terms-card-info">
                                                        {t.createdByName && (
                                                            <span className="created-by">By: {t.createdByName}</span>
                                                        )}
                                                        <span className="created-at">{formatDate(t.createdAt)}</span>
                                                    </div>
                                                </div>

                                                {/* Content Preview */}
                                                <div className="terms-content-preview">
                                                    <div className="content-block">
                                                        <label>English</label>
                                                        <p className={isExpanded ? '' : 'clamped'}>
                                                            {t.contentEn || '—'}
                                                        </p>
                                                    </div>
                                                    <div className="content-block rtl">
                                                        <label>العربية</label>
                                                        <p className={isExpanded ? '' : 'clamped'} dir="rtl">
                                                            {t.contentAr || '—'}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Show More / Less */}
                                                <button
                                                    className="btn-toggle-expand"
                                                    onClick={() => setExpandedId(isExpanded ? null : t.id)}
                                                >
                                                    {isExpanded ? 'Show less ▲' : 'Show full content ▼'}
                                                </button>

                                                {/* Card Actions */}
                                                <div className="terms-card-actions">
                                                    <button className="btn-edit-terms" onClick={() => handleEdit(t)}>
                                                        {React.createElement(IconEdit2, { size: 14 })} Edit
                                                    </button>
                                                    <button
                                                        className="btn-delete-terms"
                                                        onClick={() => handleDelete(t.id, t.version, t.type)}
                                                    >
                                                        {React.createElement(IconTrash2, { size: 14 })} Delete
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ============================
          VIEW: Acceptance Logs
      ============================ */}
            {activeView === 'acceptance-logs' && (
                <div>
                    <div className="logs-toolbar">
                        <div className="logs-toolbar-left">
                            <span className="logs-count-badge">
                                {React.createElement(IconList, { size: 14 })}
                                {logs.length} Records
                            </span>
                        </div>
                        <div className="logs-toolbar-actions">
                            <button className="btn-refresh-terms" onClick={loadLogs} disabled={loadingLogs}>
                                {React.createElement(IconRefreshCw, { className: loadingLogs ? 'spinning' : '', size: 16 })}
                                Refresh
                            </button>
                            <button className="btn-export-excel" onClick={handleExportExcel}>
                                {React.createElement(IconDownload, { size: 16 })}
                                Export Excel
                            </button>
                        </div>
                    </div>

                    {logsError && (
                        <div className="terms-error">
                            {React.createElement(IconX, { size: 16 })} {logsError}
                        </div>
                    )}

                    {loadingLogs ? (
                        <div className="terms-loading">
                            <div className="spinner" />
                            <p>Loading acceptance logs…</p>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="terms-empty">
                            {React.createElement(IconUsers, { size: 48 })}
                            <p>No acceptance logs found.</p>
                        </div>
                    ) : (
                        <div className="logs-table-wrapper">
                            <table className="logs-table">
                                <thead>
                                    <tr>
                                        <th>Log ID</th>
                                        <th>User</th>
                                        <th>Role</th>
                                        <th>Description</th>
                                        <th>Terms Version</th>
                                        <th>Terms Type</th>
                                        <th>Accepted At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log) => (
                                        <tr key={log.logId}>
                                            <td>
                                                <span className="log-id">#{log.logId}</span>
                                            </td>
                                            <td>
                                                <div className="log-user">
                                                    <span className="user-name">{log.userName || '—'}</span>
                                                    {log.userId && (
                                                        <span className="user-id">ID: {log.userId}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={typeBadgeClass(log.userRole || '')}>
                                                    {log.userRole || '—'}
                                                </span>
                                            </td>
                                            <td className="log-description">{log.description}</td>
                                            <td>
                                                {log.metadata?.termsVersion ? (
                                                    <span className="version-badge">v{log.metadata.termsVersion}</span>
                                                ) : '—'}
                                            </td>
                                            <td>
                                                {log.metadata?.termsType ? (
                                                    <span className={typeBadgeClass(log.metadata.termsType)}>
                                                        {log.metadata.termsType}
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td className="log-date">
                                                {log.acceptedAt ? formatDate(log.acceptedAt) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/* ────────────────────────────────────────────
   Reusable form sub-component
──────────────────────────────────────────── */
interface TermsFormProps {
    formData: CreateTermsRequest;
    setFormData: React.Dispatch<React.SetStateAction<CreateTermsRequest>>;
    onSubmit: () => void;
    onCancel: () => void;
    submitLabel: string;
}

const TermsForm: React.FC<TermsFormProps> = ({
    formData, setFormData, onSubmit, onCancel, submitLabel,
}) => {
    const IconSaveTyped = FiSave as any;
    const IconXTyped = FiX as any;

    return (
        <div className="terms-form">
            <div className="terms-form-row">
                <div className="terms-form-group">
                    <label>Type *</label>
                    <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as 'USER' | 'TECHNICIAN' })}
                    >
                        <option value="USER">User</option>
                        <option value="TECHNICIAN">Technician</option>
                    </select>
                </div>
                <div className="terms-form-group">
                    <label>Version *</label>
                    <input
                        type="text"
                        value={formData.version}
                        onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                        placeholder="e.g. 1.0"
                    />
                </div>
                <div className="terms-form-group checkbox-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={formData.isActive !== false}
                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        />
                        Active
                    </label>
                </div>
            </div>

            <div className="terms-form-row">
                <div className="terms-form-group full-width">
                    <label>Content (English) *</label>
                    <textarea
                        value={formData.contentEn}
                        onChange={(e) => setFormData({ ...formData, contentEn: e.target.value })}
                        placeholder="Enter terms and conditions content in English…"
                        rows={8}
                    />
                </div>
                <div className="terms-form-group full-width">
                    <label>Content (Arabic) *</label>
                    <textarea
                        value={formData.contentAr}
                        onChange={(e) => setFormData({ ...formData, contentAr: e.target.value })}
                        placeholder="أدخل الشروط والأحكام بالعربية…"
                        rows={8}
                        dir="rtl"
                    />
                </div>
            </div>

            <div className="terms-form-actions">
                <button className="btn-save-terms" onClick={onSubmit}>
                    {React.createElement(IconSaveTyped, { size: 16 })} {submitLabel}
                </button>
                <button className="btn-cancel-terms" onClick={onCancel}>
                    {React.createElement(IconXTyped, { size: 16 })} Cancel
                </button>
            </div>
        </div>
    );
};

export default TermsAndConditions;
